import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Invoice as a Personal Trainer in the UK — HMRC, VAT & Payment Terms',
      'description': 'A complete guide for UK personal trainers: what to put on an invoice, VAT rules, HMRC self-assessment, and the best payment terms for session-based and retainer clients.',
      'url': 'https://www.invilic.com/blog/how-to-invoice-as-personal-trainer-uk',
      'datePublished': '2026-06-07',
      'dateModified': '2026-06-07',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Do personal trainers in the UK have to charge VAT?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Only if your annual taxable turnover exceeds £90,000 (the 2024/25 VAT registration threshold). Below this, you cannot charge VAT and must not add it to your invoices. Once you exceed £90,000 you must register for VAT with HMRC and charge 20% on all PT sessions, since personal training is a standard-rated service — unlike sports participation which can be VAT exempt.' },
        },
        {
          '@type': 'Question',
          'name': 'Do UK personal trainers need to do Self Assessment?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes. If you are self-employed or earn more than £1,000 from personal training in a tax year (after April 5), you must register for Self Assessment with HMRC and file an annual tax return. You pay income tax on your profits and Class 4 National Insurance (9% on profits between £12,570 and £50,270; 2% above). Class 2 NI was abolished in April 2024. Register by 5 October following the end of the tax year in which you started.' },
        },
        {
          '@type': 'Question',
          'name': 'What should a UK personal trainer invoice include?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A UK personal trainer invoice must include: your full name or trading name, your address, the client name and address, a unique sequential invoice number, the invoice date, the payment due date, a description of services (e.g. "10 × 60-minute PT sessions, March 2026"), the session rate and total amount, and your UK bank details (sort code and account number). If you are VAT registered, also include your VAT registration number and show 20% VAT separately.' },
        },
        {
          '@type': 'Question',
          'name': 'Should personal trainers invoice per session or monthly?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Both work, but monthly invoicing is more professional and reduces admin. For regular clients, invoice at the start of each month for the sessions agreed that month — or at the end of the month once sessions are complete. For block bookings (e.g. 10 sessions prepaid), invoice the full block upfront. For corporate gym clients, invoice monthly with Net 30 payment terms. For private clients, consider requesting payment before or immediately after each session.' },
        },
        {
          '@type': 'Question',
          'name': 'Can personal trainers claim expenses to reduce their tax bill?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes. Allowable expenses that reduce your taxable profit include: professional insurance (REPs/CIMSPA), gym membership if wholly used for business, CPD courses and fitness certifications, equipment (barbells, resistance bands, foam rollers), fitness software and apps, professional clothing with your brand logo, marketing and website costs, and a portion of phone costs for business use. Keep receipts for everything and record expenses alongside your invoice records.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const invoiceFields = [
  { field: 'Your full name or trading name', note: 'Use your legal name as a sole trader, or your limited company name if applicable' },
  { field: 'Your address and contact details', note: 'Required on all UK business invoices; can be a home or studio address' },
  { field: "Client's name and address", note: 'Full name and address of the client being billed' },
  { field: 'A unique sequential invoice number', note: 'e.g. INV-2026-001 — never reuse numbers; HMRC requires sequential numbering' },
  { field: 'Invoice date', note: 'The date the invoice is issued, not the date of the sessions' },
  { field: 'Payment due date', note: 'e.g. "Due within 7 days" or a specific date such as "Due: 14 June 2026"' },
  { field: 'Description of sessions', note: 'e.g. "8 × 60-minute personal training sessions — June 2026"' },
  { field: 'Rate per session and total', note: 'e.g. £50 × 8 sessions = £400 total' },
  { field: 'VAT number and 20% VAT (if registered)', note: 'Only required if you are VAT-registered with HMRC; show net, VAT, and gross separately' },
  { field: 'UK bank details (sort code + account number)', note: 'Include bank name, account name, 6-digit sort code, and 8-digit account number' },
];

export default function HowToInvoicePersonalTrainerUkPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Invoice as a Personal Trainer in the UK — HMRC, VAT & Payment Terms | Invilic"
        description="Complete guide for UK personal trainers: invoice requirements, HMRC self-assessment, 20% VAT threshold, session vs monthly billing, and allowable expenses."
        canonical="https://www.invilic.com/blog/how-to-invoice-as-personal-trainer-uk"
        ogType="article"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
              UK Invoicing
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">6 min read · June 7, 2026</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-6">
            How to Invoice as a Personal Trainer in the UK — HMRC, VAT & Payment Terms
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 leading-relaxed">
            Most UK personal trainers are self-employed sole traders. Whether you train clients in their homes, in a gym, or online, you need to invoice correctly to satisfy HMRC and get paid on time. This guide covers what goes on your invoice, when VAT applies, and the payment terms that work best for PTs.
          </p>
        </div>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">What Must Be on a UK Personal Trainer Invoice?</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            HMRC requires sole traders to keep proper records. Your invoice is the primary record of the service you provided and the amount charged.
          </p>
          <div className="space-y-3">
            {invoiceFields.map(({ field, note }) => (
              <div key={field} className="flex items-start gap-3 p-4 rounded-xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <CheckCircle className="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{field}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{note}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">VAT Rules for UK Personal Trainers</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-5">
            Personal training services are <strong className="text-gray-900 dark:text-white">standard-rated at 20% VAT</strong>. But you only charge VAT once you are VAT registered, which is only required once you exceed the threshold.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {[
              { label: 'VAT registration threshold', value: '£90,000/year', note: 'If your turnover crosses £90,000 in any rolling 12 months, you must register. Register within 30 days of exceeding the threshold.' },
              { label: 'VAT rate on PT sessions', value: '20% standard rate', note: 'PT services are standard-rated at 20%. Unlike gym membership or sports facilities, personal training is not VAT exempt.' },
              { label: 'Online PT sessions', value: 'Also standard-rated', note: 'Whether delivered in person or via Zoom, PT coaching is standard-rated at 20% once you are VAT registered.' },
              { label: 'Health/sports coaching exemption', value: 'Usually does not apply', note: 'The VAT exemption for sports participation applies to the right to use a facility, not personal training services. Most PTs are standard-rated.' },
            ].map(({ label, value, note }) => (
              <div key={label} className="p-4 rounded-xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
                <p className="font-bold text-violet-700 dark:text-violet-300 text-sm mb-1">{value}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">{note}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Best Payment Terms for Personal Trainers</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-5">
            How you structure your invoicing affects how quickly you get paid and how much of your time is spent chasing payments.
          </p>
          <div className="space-y-4">
            {[
              {
                model: 'Per-session billing',
                when: 'Good for new or infrequent clients',
                tip: 'Invoice immediately after the session, or request payment before you begin. Due upon receipt or same-day keeps cash flow tight. Downside: high admin if clients train multiple times per week.',
              },
              {
                model: 'Block booking (prepaid)',
                when: 'Best for regular private clients',
                tip: 'Invoice the full block upfront (e.g. 10 sessions). Client pays before sessions begin. This protects against cancellations and gives you predictable income. Specify your cancellation and expiry policy on the invoice.',
              },
              {
                model: 'Monthly retainer',
                when: 'Best for online coaching clients',
                tip: 'Invoice on the same date each month for the agreed number of sessions or coaching hours. Net 7 (pay within 7 days) works well. Set up a direct debit via GoCardless to automate collection.',
              },
              {
                model: 'Corporate clients',
                when: 'For gym or workplace wellness contracts',
                tip: 'Corporate clients have internal payment processes. Use Net 30 payment terms. Invoice on the 1st of each month for the prior month\'s sessions. Include a purchase order number if the client requires it.',
              },
            ].map(({ model, when, tip }) => (
              <div key={model} className="p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-gray-900 dark:text-white">{model}</p>
                  <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-lg">{when}</span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{tip}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">HMRC Self Assessment for Personal Trainers</h2>
          <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
            <p>As a self-employed PT, you must register for Self Assessment with HMRC if you earn more than £1,000 from self-employment in a tax year. Register at gov.uk/register-for-self-assessment.</p>
            <p>You pay <strong className="text-gray-900 dark:text-white">income tax</strong> on your profits (income minus allowable expenses): 20% basic rate up to £50,270 and 40% higher rate above that (2024/25).</p>
            <p>You also pay <strong className="text-gray-900 dark:text-white">Class 4 National Insurance</strong>: 6% on profits between £12,570 and £50,270, and 2% above £50,270.</p>
            <p>Keep records of all your invoices, expenses, and payments for at least <strong className="text-gray-900 dark:text-white">6 years</strong>. HMRC can investigate records up to this period.</p>
            <p>The Self Assessment tax return deadline is <strong className="text-gray-900 dark:text-white">31 January</strong> each year for the prior tax year (which runs April 6 to April 5).</p>
          </div>
        </section>

        <BlogCTA
          heading="Create a Personal Trainer Invoice for Free"
          body="Professional UK PDF invoices with correct fields for sessions, blocks, and retainers. No account needed."
          linkTo="/personal-trainer-invoice-template"
          linkText="Get Free PT Invoice Template"
        />

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <RelatedArticles current="/blog/how-to-invoice-as-personal-trainer-uk" />
      </main>
    </div>
  );
}
